import { useCallback, useEffect, useState } from "react";
import {
  classRoomSay,
  classesSummary,
  classMeetChip,
  classMeetSummary,
  TEACHER_STEPS,
  RETENTION_CHOICES,
  retentionAdminSay,
  retentionNeedsConfirm,
  retentionConfirmSay,
  sweepIsStale,
  sweepStaleSay,
  retentionSay,
  daysLeft,
  type ClassRoomRow,
} from "@greg-chick/shared";
import { adminApi, adminToken, contentBase } from "../lib/contentApi";
import { Empty, Msg, PageHead } from "./ui";

/**
 * 🏫 **방과후 수업** — 어느 반이 돌아가고 있는지.
 *
 * ## 왜 필요한가
 * 수업 기능은 다 만들어 놨는데 **출판사가 그걸 볼 방법이 없었다.** 반이 몇 개인지,
 * 아이가 몇 명인지, 그림책이 몇 권 나왔는지 모르면 「잘 되고 있나요」에 답할 수 없고,
 * 학교에 팔 수도 지원할 수도 없다.
 *
 * ## 🔴 여기에 아이 정보는 없다
 * 반 코드와 **숫자**뿐이다. 별명조차 서버가 안 보낸다(불변식 1) —
 * 운영자가 알아야 하는 건 규모지 누가 썼는지가 아니다.
 * 실제 그림책을 보려면 [전시장 열기]로 아이 화면과 **똑같은 것**을 본다.
 *
 * ## 🗑 지우는 길도 여기 있다(FR-CLASS-09)
 * 아이 글이 영구히 쌓이고 있었고 지우는 길이 없었다. 학교 담당자는 계약 전에 반드시
 * 「언제 지워지나요」를 묻는다 — 답할 것이 있어야 한다.
 *
 * 🔴 **[내려받기]가 [지우기]보다 먼저 있다.** 아이가 만든 것을 없애기만 하는 화면은
 * 만들면 안 된다. 그림책은 아이 것이다 — 들고 갈 수 있어야 지우는 것이 정당해진다.
 *
 * 🔴 지우기는 **반 코드를 직접 쳐야** 된다. 되돌릴 수 없는 일에 한 번 누름은 너무 가볍다.
 */
export function ClassRoomsPanel() {
  const [rows, setRows] = useState<ClassRoomRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [months, setMonths] = useState(0);
  /**
   * 🔴 **정해 둔 보존 기간이 실제로 지켜지고 있나.**
   *
   * 정리는 매일 밤 서버가 혼자 돈다. 실패해도 예전엔 로그로만 남아서 **아무도 몰랐다** —
   * 그 사이 「3개월 뒤 지움」으로 정해 둔 아이 글이 계속 남는다.
   * 보호자에게 한 약속이 조용히 깨지는데 화면에는 아무 표시가 없었다.
   *
   * `null` 은 **「한 번도 안 돌았다」**이지 「이상 없다」가 아니다.
   */
  const [sweep, setSweep] = useState<{ at: string; ok: boolean; n: number; error?: string } | null>(
    null,
  );
  /** 지우려고 연 반 + 운영자가 친 코드(되돌릴 수 없으니 직접 쳐야 한다). */
  const [killing, setKilling] = useState<string | null>(null);
  const [typed, setTyped] = useState("");
  const now = new Date();

  const api = useCallback(async (path: string, init?: RequestInit) => {
    const res = await fetch(`${adminApi()}${path}`, {
      ...init,
      headers: { ...(init?.headers ?? {}), "x-admin-token": adminToken() },
      cache: "no-store",
    });
    if (!res.ok) throw new Error(String(res.status));
    return res.json() as Promise<Record<string, unknown>>;
  }, []);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const res = await fetch(`${adminApi()}/admin/classes`, {
        headers: { "x-admin-token": adminToken() },
        cache: "no-store",
      });
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as { classes?: ClassRoomRow[] };
      setRows(body.classes ?? []);
    } catch {
      // 못 받았을 때 목록을 비우지 않는다 — 「반이 다 사라졌다」로 읽힌다
      setErr("지금 못 불러왔어요. 잠시 뒤에 다시 눌러 주세요.");
    }
  }, []);

  useEffect(() => {
    void load();
    void api("/admin/class-policy")
      .then((b) => {
        setMonths(Number(b.months) || 0);
        setSweep((b.sweep as typeof sweep) ?? null);
      })
      .catch(() => {
        /* 못 불러왔으면 「안 지움」으로 둔다 — 지우는 쪽으로 틀리면 안 된다 */
      });
  }, [load, api]);

  /** 🔴 보존 기간을 바꾸면 **보호자 안내 문장도 같이 바뀐다** — 둘이 갈리지 않게. */
  async function saveMonths(m: number) {
    /**
     * 🔴 **더 빨리 지우게 되는 쪽이면 한 번 물어본다.**
     *
     * 기간은 라디오 단추라 한 번 누르면 바로 저장된다(안 누른 편집물이 안 생기니 그 자체는
     * 좋다). 그런데 잘못 누르면 **그날 밤 서버가 혼자 도는 정리**가 그보다 오래된 반을
     * 통째로 지운다 — 아이가 한 학기 만든 글이다. 눌렀다는 것조차 모를 수 있다.
     *
     * 늘리거나 「안 지워요」로 되돌리는 것은 안전하니 안 묻는다(판정은 shared 한 곳).
     */
    if (retentionNeedsConfirm(months, m) && !window.confirm(retentionConfirmSay(m))) return;
    setErr(null);
    setOk(null);
    try {
      await api("/admin/class-policy", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ months: m }),
      });
      setMonths(m);
      setOk("보존 기간을 저장했어요. 보호자 안내에 나가는 문장도 같이 바뀌었어요.");
    } catch {
      setErr("보존 기간을 저장하지 못했어요. 잠시 뒤에 다시 눌러 주세요.");
    }
  }

  /** 지우기 전에 들고 갈 수 있게 — 파일로 내려준다. */
  async function exportClass(cls: string) {
    setErr(null);
    setOk(null);
    try {
      const body = await api(`/admin/class-export?cls=${encodeURIComponent(cls)}`);
      const blob = new Blob([JSON.stringify(body, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${cls}-그림책.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      const n = Array.isArray(body.stories) ? body.stories.length : 0;
      /**
       * 🔴 **못 읽은 글이 있으면 지우기 전에 말한다.**
       *
       * 이 파일이 마지막 한 벌이다 — 운영자는 보통 내려받고 나서 지운다. 깨진 글을
       * 조용히 빈 글로 넣으면, 파일에는 그 아이 이름이 멀쩡히 있고 글만 비어 있어서
       * 다 받은 줄 알고 지운다. 그러면 되돌릴 수 없다.
       * (날 것은 파일 안에 `rawPages` 로 같이 들어 있다 — 되살릴 여지를 남긴다.)
       */
      const bad = typeof body.damaged === "number" ? body.damaged : 0;
      setOk(
        `${cls} 반의 그림책 ${n}권을 내려받았어요.` +
          (bad
            ? ` ⚠️ 그중 ${bad}권은 글을 못 읽었어요 — 파일 안에 원본이 그대로 들어 있어요. 지우기 전에 꼭 확인해 주세요.`
            : ""),
      );
    } catch {
      setErr("내려받지 못했어요. 잠시 뒤에 다시 눌러 주세요.");
    }
  }

  async function deleteClass(cls: string) {
    setErr(null);
    setOk(null);
    try {
      const b = await api("/admin/class-delete", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ classCode: cls }),
      });
      // 🔴 몇 개를 지웠는지 그대로 말한다 — 「지웠어요」만 말하면 0개일 때를 아무도 모른다
      setOk(`${cls} 반을 지웠어요 (그림책 ${b.stories ?? 0}권 · 아이 ${b.members ?? 0}명).`);
      setKilling(null);
      setTyped("");
      await load();
    } catch {
      setErr("지우지 못했어요. 잠시 뒤에 다시 눌러 주세요.");
    }
  }

  const list = rows ?? [];

  return (
    <section className="panel">
      <PageHead
        icon="🏫"
        title="방과후 수업"
        desc="어느 반이 돌아가고 있는지 한눈에 봐요. 아이가 누구인지는 저장하지 않아요"
      />

      <div className="classes__top">
        <strong>{rows === null ? "불러오는 중…" : classesSummary(list)}</strong>
        {/* 🎥 자동 방은 **이름만 맞히면 누구나 들어온다** — 몇 반이 남았는지가 지원할 거리다 */}
        {rows !== null && classMeetSummary(list) ? (
          <span className="classes__meetsum">{classMeetSummary(list)}</span>
        ) : null}
        <button type="button" className="btn btn--sm" onClick={() => void load()}>
          ↻ 새로고침
        </button>
      </div>
      <Msg text={err} kind="warn" />
      <Msg text={ok} kind="ok" />

      {/* ── 🗑 보존 기간 ─────────────────────────────────── */}
      <div className="keep">
        <h2 className="keep__title">
          아이 글을 언제까지 두나요{" "}
          <span className="keep__hint">학교가 계약 전에 반드시 물어봐요</span>
        </h2>
        <div className="keep__pick" role="radiogroup" aria-label="보존 기간">
          {RETENTION_CHOICES.map((c) => (
            <button
              key={c.months}
              type="button"
              role="radio"
              aria-checked={months === c.months}
              className="btn btn--sm"
              onClick={() => void saveMonths(c.months)}
            >
              {c.label}
            </button>
          ))}
        </div>
        <p className="keep__say" data-none={months <= 0 ? "1" : "0"}>
          {retentionAdminSay(months)}
        </p>
        {/*
          🔴 **약속이 지켜지고 있는지.** 기간을 정해 두면 「되고 있겠지」가 된다 —
          정리는 서버가 밤에 혼자 도는 일이라 눈에 보이지 않는다.
          안 지움(0)이면 지울 것이 없으니 이 줄도 안 띄운다.
        */}
        {months > 0 ? (
          <p
            className="keep__sweep"
            /**
             * 🔴 **성공했어도 오래됐으면 빨갛다.**
             *
             * 예전엔 「한 번도 안 돌았다」와 「실패했다」만 빨갛게 했다. 그래서 성공한 뒤
             * 크론이 죽으면 **굳은 날짜가 평범한 글씨로** 계속 떠 있었다 —
             * 운영자는 날짜를 보고 「되고 있구나」로 읽는다. 이 줄을 만든 이유가
             * 「되고 있겠지」를 막는 것인데, 정작 제일 조용한 경우를 안 보고 있었다.
             */
            data-bad={!sweep || !sweep.ok || sweepIsStale(sweep.at, Date.now()) ? "1" : "0"}
          >
            {!sweep ? (
              <>
                <b>아직 한 번도 정리가 안 돌았어요.</b> 하루 뒤에도 이 문구가 그대로면
                개발자에게 알려 주세요 — 정한 기간이 안 지켜지고 있는 거예요.
              </>
            ) : sweep.ok ? (
              <>
                마지막 정리 <b>{new Date(sweep.at).toLocaleString("ko-KR")}</b>
                {sweep.n > 0 ? ` — 기간이 지난 반 ${sweep.n}개를 지웠어요` : " — 지울 반이 없었어요"}
                {sweepStaleSay(sweep.at, Date.now()) ? (
                  <>
                    <br />
                    <b>{sweepStaleSay(sweep.at, Date.now())}</b>
                  </>
                ) : null}
              </>
            ) : (
              <>
                <b>마지막 정리가 실패했어요</b>({new Date(sweep.at).toLocaleString("ko-KR")}).
                정한 기간이 안 지켜지고 있어요 — 개발자에게 알려 주세요.
              </>
            )}
          </p>
        ) : null}

        {/* 🔴 보호자가 읽을 문장을 **여기서 그대로 보여 준다.** 따로 써 두면 갈린다 */}
        <p className="keep__parent">
          <b>보호자 안내에 이렇게 나가요</b>
          <span>{retentionSay(months)}</span>
        </p>
      </div>

      {rows !== null && list.length === 0 ? (
        <Empty icon="🏫">
          아직 열린 반이 없어요.
          <br />
          선생님이 웹의 <b>[🧑‍🏫 선생님 화면]</b>에서 <b>[＋ 새 반 만들기]</b>를 누르면 여기 떠요.
        </Empty>
      ) : null}

      {list.length > 0 ? (
        <ul className="classes__list">
          {list.map((r) => (
            <li key={r.classCode} className="classes__row">
              <span className="classes__code">{r.classCode}</span>
              <span className="classes__say">{classRoomSay(r)}</span>
              {/* 🎥 선생님이 제 방을 넣은 반만 표시한다 — 자동 방에 경고를 달면 목록이
                  통째로 빨개지고 곧 아무도 안 본다(전체 그림은 위 한 줄로 준다). */}
              {classMeetChip(r) ? <span className="classes__meet">{classMeetChip(r)}</span> : null}
              <span className="classes__when">{r.lastAt ? whenSay(r.lastAt) : "—"}</span>
              {(() => {
                const left = daysLeft(r.lastAt, months, now);
                return left !== null && left <= 30 ? (
                  <span className="classes__soon">
                    {left === 0 ? "곧 지워져요" : `${left}일 뒤 지워져요`}
                  </span>
                ) : null;
              })()}
              <a
                className="btn btn--sm"
                href={galleryUrl(r.classCode)}
                target="_blank"
                rel="noreferrer"
              >
                🖼️ 전시장 열기
              </a>
              {/* 🔴 내려받기가 먼저다 — 없애기만 하는 화면은 만들면 안 된다 */}
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => void exportClass(r.classCode)}
              >
                ⬇ 내려받기
              </button>
              <button
                type="button"
                className="btn btn--sm btn--danger"
                onClick={() => {
                  setKilling(killing === r.classCode ? null : r.classCode);
                  setTyped("");
                }}
              >
                🗑 지우기
              </button>
              {killing === r.classCode ? (
                <div className="kill">
                  <p className="kill__warn">
                    <b>되돌릴 수 없어요.</b> 그림책 {r.done + r.writing}권과 아이 {r.members}명의
                    기록이 사라져요. 먼저 <b>[⬇ 내려받기]</b>를 눌러 두세요.
                  </p>
                  <label className="kill__ask">
                    지우려면 반 코드를 그대로 쳐 주세요
                    <input
                      className="input"
                      value={typed}
                      onChange={(e) => setTyped(e.target.value)}
                      placeholder={r.classCode}
                      aria-label="반 코드 확인"
                    />
                  </label>
                  <div className="kill__acts">
                    <button
                      type="button"
                      className="btn btn--sm btn--danger"
                      disabled={typed.trim() !== r.classCode}
                      onClick={() => void deleteClass(r.classCode)}
                    >
                      정말 지우기
                    </button>
                    <button type="button" className="btn btn--sm" onClick={() => setKilling(null)}>
                      그만두기
                    </button>
                  </div>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {/* 문의가 오면 여기를 읽어 주면 된다 — 선생님 화면과 **같은 글**이다(shared) */}
      <details className="classes__how">
        <summary>선생님께 안내할 순서</summary>
        <ol>
          {TEACHER_STEPS.map((s) => (
            <li key={s.n}>
              {s.say}
              {s.why ? <span className="classes__why">{s.why}</span> : null}
            </li>
          ))}
        </ol>
      </details>
    </section>
  );
}

/**
 * 아이 화면의 전시장을 그대로 연다.
 *
 * 🔴 관리자용 미리보기를 따로 만들지 않는다 — 따로 만들면 운영자가 본 것과 아이가 본 것이
 * 갈리고, 「관리자에선 되는데 화면은 안 바뀐다」가 시작된다.
 */
function galleryUrl(classCode: string): string {
  return `${contentBase()}/v?cls=${encodeURIComponent(classCode)}&open=gallery`;
}

/** 「3일 전」처럼. 운영자는 시각이 아니라 **얼마나 됐나**를 본다. */
function whenSay(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  const min = Math.floor((Date.now() - t) / 60000);
  if (min < 1) return "방금";
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.floor(hr / 24);
  return day < 30 ? `${day}일 전` : `${Math.floor(day / 30)}달 전`;
}
